/**
 * @file PresetScheme.tsx
 * @description Мини-схема заготовки секции для библиотеки «Добавить блок»
 * (phase19.16 Р2; uiux-design-r2 §5; SB27): раскладка рисуется из самого
 * payload заготовки (колонки по пропорциям, элементы фигурами), а цвета —
 * переменными ролей текущего вида сайта `--site-color-<роль>` (их ставит
 * окно библиотеки). Новая заготовка получает схему без правки этого файла.
 */

import type { SectionElement, SectionPayload } from "@chaiberi/shared";

const W = 160;
const H = 100;
const PAD = 10;
const GAP = 6;

/** Цвет роли сайта: переменную ставит окно библиотеки. */
const role = (name: string) => `var(--site-color-${name})`;

/** Высота фигуры элемента в схеме. */
const ELEMENT_HEIGHT: Readonly<Record<SectionElement["kind"], number>> = {
  heading: 7,
  eyebrow: 3,
  text: 10,
  image: 34,
  gallery: 18,
  video: 26,
  audio: 6,
  button: 7,
  tiles: 26,
  products: 26,
  categories: 22,
  articles: 22,
  reviews: 18,
  faq: 18,
  contacts: 12,
  newsletter: 8,
  announcements: 5,
  stats: 12,
  table: 22,
  // Р2а: элементы данных товара (раскладки страницы товара).
  breadcrumbs: 3,
  "product-gallery": 40,
  "product-title": 7,
  "product-buy": 14,
  "product-badges": 3,
  "product-short": 4,
  "product-description": 10,
  "product-facts": 14,
  "product-specs": 12,
  "product-tags": 3,
  "product-reviews": 16,
  "product-related": 22,
  "product-seller": 6,
};

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function ElementShape({ element, box, dark }: { element: SectionElement; box: Box; dark: boolean }) {
  const ink = dark ? role("onDark") : role("text");
  const muted = dark ? role("onDark") : role("textMuted");
  const align = element.align ?? "left";
  const lineW = (k: number) => box.w * k;
  const lineX = (w: number) => (align === "center" ? box.x + (box.w - w) / 2 : align === "right" ? box.x + box.w - w : box.x);
  switch (element.kind) {
    case "heading":
    case "product-title": {
      const w = lineW(element.kind === "product-title" || element.level === 1 ? 0.9 : 0.7);
      return <rect x={lineX(w)} y={box.y} width={w} height={box.h - 1} rx={1} fill={ink} />;
    }
    case "eyebrow":
    case "announcements":
    case "breadcrumbs":
    case "product-badges":
    case "product-tags": {
      const w = lineW(0.4);
      return <rect x={lineX(w)} y={box.y} width={w} height={2} rx={1} fill={role("accent")} />;
    }
    case "text":
    case "product-description":
      return (
        <g fill={muted} opacity={0.7}>
          {[0, 3.5, 7].map((dy, i) => {
            const w = lineW(i === 2 ? 0.6 : 0.95);
            return <rect key={dy} x={lineX(w)} y={box.y + dy} width={w} height={1.6} rx={0.8} />;
          })}
        </g>
      );
    case "button": {
      const w = Math.min(36, box.w * 0.6);
      return element.variant === "link" ? (
        <rect x={lineX(w)} y={box.y + 4} width={w * 0.7} height={1.6} fill={role("button")} />
      ) : (
        <rect x={lineX(w)} y={box.y} width={w} height={box.h - 1} rx={2} fill={element.variant === "outline" ? "none" : role("button")} stroke={role("button")} strokeWidth={1} />
      );
    }
    case "tiles":
    case "products":
    case "categories":
    case "articles":
    case "gallery":
    case "product-related": {
      const n = element.kind === "tiles" || element.kind === "categories" ? element.cols : element.kind === "products" || element.kind === "product-related" ? element.perRow : 3;
      const cw = (box.w - (n - 1) * 3) / n;
      return (
        <g>
          {Array.from({ length: n }, (_, i) => (
            <rect key={i} x={box.x + i * (cw + 3)} y={box.y} width={cw} height={box.h - 1} rx={1.5} fill={role("cardBg")} stroke={muted} strokeOpacity={0.25} />
          ))}
        </g>
      );
    }
    default:
      // Фото, видео, отзывы, вопросы, контакты, подписка — плашкой.
      return <rect x={box.x} y={box.y} width={box.w} height={box.h - 1} rx={2} fill={role("panelBg")} stroke={muted} strokeOpacity={0.25} />;
  }
}

/** Фон секции в схеме: роль, фото (тёмная роль) или фон страницы. */
function sectionFill(payload: SectionPayload): { fill: string; dark: boolean } {
  const bg = payload.look.bg;
  if (bg.kind === "role") return { fill: role(bg.role), dark: bg.role === "darkBg" || bg.role === "accent" };
  if (bg.kind === "media") return { fill: role("darkBg"), dark: true };
  return { fill: role("pageBg"), dark: false };
}

export function PresetScheme({ payload }: { payload: SectionPayload }) {
  const { fill, dark } = sectionFill(payload);
  const parts = payload.columns.shares;
  const total = parts.reduce((a, b) => a + b, 0) || 1;
  const innerW = W - PAD * 2 - GAP * (parts.length - 1);
  const headerH = payload.header ? 10 : 0;
  let x = PAD;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block h-full w-full" aria-hidden>
      <rect width={W} height={H} fill={fill} />
      {payload.header ? (
        <g>
          <rect x={PAD} y={PAD} width={40} height={5} rx={1} fill={dark ? role("onDark") : role("text")} />
          {payload.header.line.width > 0 ? <rect x={PAD + 46} y={PAD + 2} width={W - PAD * 2 - 72} height={0.8} fill={role("textMuted")} /> : null}
        </g>
      ) : null}
      {payload.columns.items.map((column, i) => {
        const w = (innerW * (parts[i] ?? 1)) / total;
        const colX = x;
        x += w + GAP;
        const heights = column.elements.map((el) => ELEMENT_HEIGHT[el.kind] + 3);
        const used = heights.reduce((a, b) => a + b, 0);
        const free = H - PAD * 2 - headerH;
        let y = PAD + headerH + (payload.columns.valign === "center" ? Math.max(0, (free - used) / 2) : payload.columns.valign === "bottom" ? Math.max(0, free - used) : 0);
        return (
          <g key={i}>
            {column.bg ? <rect x={colX - 2} y={PAD - 2} width={w + 4} height={H - PAD * 2 + 4} rx={2} fill={column.bg.kind === "role" ? role(column.bg.role) : column.bg.color} /> : null}
            {column.elements.length === 0 ? (
              <rect x={colX} y={PAD + headerH} width={w} height={free} rx={2} fill="none" stroke={role("textMuted")} strokeOpacity={0.4} strokeDasharray="3 2" />
            ) : null}
            {column.elements.map((element, j) => {
              const h = heights[j] ?? 6;
              const box = { x: colX, y, w, h: h - 3 };
              y += h;
              return <ElementShape key={element.id} element={element} box={box} dark={dark} />;
            })}
          </g>
        );
      })}
    </svg>
  );
}
